import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IHttpOptions } from '../interfaces/httpOptions.interface';
import { ILink } from '../interfaces/link.interface';


import { IHateoas } from './hateoas.interface';

/**
 * @internal
 */
@Injectable({
  providedIn: 'root',
})
export class HateoasService {
  entryPoint: string = '';
  httpOptions: IHttpOptions = {};
  links: ILink[] = [];

  /**
   * Método que retorna uma url baseado na relation informada.
   *
   * Method that returns a url based on the inputed relation.
   *
   * @param relation Recebe o atributo 'rel' do retorno da API no padrão HATEOAS
   */
  getURL(relation: string): string {
    return this.isEntryPoint(relation)
      ? relation
      : this.getLinkOnState(relation);
  }

  /**
   * Método que returna um novo link armazenado em memória basedo na relation.
   *
   * Method that returns a new link based on the relation.
   * @internal
   * @param relation  Recebe o atributo 'rel' do retorno da API no padrão HATEOAS
   * @returns a link `href` based on relation param.
   */
  private getLinkOnState(relation: string): string {
    const link = this.getLink(relation)?.href;

    if (link) {
      return link;
    }

    throw new Error(`Nenhum Link com a relation '${relation}' foi encontrado`);
  }

  /**
   * Método que verifica se url inputada é um entry point.
   *
   * Method that verifies if inputed url is an entry point.
   *
   * @internal
   * @param url string
   */
  private isEntryPoint(url: string) {
    return url === this.entryPoint || url.startsWith('http');
  }

  /**
   * Método que retorna todas as relations e links existentes em memória.
   *
   * Method that returns all relations and links in memory.
   *
   * @returns all `Links` for hateoas relations.
   */
  getRelations(): ILink[] {
    return this.links;
  }

  /**
   * Método que retorna uma lista de links para determinada relation.
   *
   * Method that returns a links list for inputed relation.
   *
   * @internal
   * @param relation  Recebe o atributo 'rel' do retorno da API no padrão HATEOAS
   * @returns a `Link` if relations existis.
   */
  getLink(relation: string): ILink | undefined {
    const linksLength = !this.links?.length;
    if (linksLength) {
      return undefined;
    }
    const [link] = this.links.filter(l => l.rel === relation);
    return link;
  }

  /**
   * Método para adicionar links em memória.
   *
   * Method that pushes a new link in memory.
   *
   * @param links Array de links retornados pela API
   */
  setLinks(links: ILink[]) {
    const isNoLinks = !links || links.length === 0;

    if (isNoLinks) {
      return;
    }

    const mergedArray = [...links,...this.links];
    // mergedArray have duplicates, lets remove the duplicates using Set
    let set = new Set();
    let unionArray = mergedArray.filter(item => {
      if (!set.has(item.rel)) {
        set.add(item.rel);
        return true;
      }
      return false;
    }, set);

    this.links = unionArray;
  }

  /**
   * Método para setar configurações padrão(IConfig) em memória dentro do HateoasService.
   *
   * Method that sets default configuration(IConfig) in memory on HateoasService.
   *
   * @internal
   * @param config Recebe as configurações de entrypoint e httpOptions da interface IHateoas
   */
  setConfig({ entryPoint: entrypoint, httpOptions }: IHateoas) {
    this.entryPoint = entrypoint || this.entryPoint;

    this.httpOptions = {
      ...this.httpOptions,
      ...httpOptions,
      headers: this.merge<IHttpOptions['headers']>(HttpHeaders, this.httpOptions?.headers, httpOptions?.headers),
      params: this.merge<IHttpOptions['params']>(HttpParams, this.httpOptions?.params, httpOptions?.params),
    };
  }

  /**
   * Método que faz o merge dos HttpOptions header ou params.
   *
   * Method that merges HttpHeaders header or params.
   *
   * @internal
   * @param c Recebe um objeto do tipo HttpHeaders ou HttpParam
   * @param current Recebe o valor do header ou param definido globalmente
   * @param additional Recebe o valor do header ou param passado via parâmetro na chamada do método
   */
  private merge<T extends IHttpOptions['headers'] | IHttpOptions['params']>(
    c: new (t?: any) => T,
    current?: T,
    additional?: T,
  ): T {
    const newCurrent = current instanceof c
      ? current
      : new c(current);

    if (!additional) {
      return newCurrent;
    }

    const newAdditional = additional instanceof c
      ? additional
      : new c(additional);

    return (newAdditional as HttpHeaders | HttpParams)
      .keys()
      .reduce(
        // tslint:disable-next-line: no-non-null-assertion
        (headers: any, key: any) => headers.set(key, (newAdditional as HttpHeaders | HttpParams).get(key)!),
        newCurrent,
      );
  }


}
